import {
	AppBar,
	Divider,
	Grid,
	IconButton,
	MenuItem,
	TextField,
	Toolbar,
	Typography,
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
} from '@mui/material'
import Transition from './Transition'
import CloseIcon from '@mui/icons-material/Close'
import SaveAsTwoToneIcon from '@mui/icons-material/SaveAsTwoTone'
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs from 'dayjs'
import {
	BoardType,
	CmmnCodeType,
	CmmoCodeShortType,
	FileItemType,
} from 'constants/types'
import { FieldErrors } from 'react-hook-form'
import LexicalEditorComponent from './LexicalEditorComponent'
import { selectDisplayMode } from 'hooks/themeMode'
import { useSelector } from 'react-redux'
import { useEffect, useRef, useCallback, useState } from 'react'
import React from 'react'
import CheckIcon from '@mui/icons-material/Check'
import FileUploader from './FileUploader'

type BoardWriteProps = {
	dialogOpen: boolean
	dialogClose: () => void
	dialogSave: () => void
	data: BoardType
	setData: React.Dispatch<React.SetStateAction<BoardType>>
	bbsType: CmmnCodeType[]
	title: string
	errors?: FieldErrors<any>
	fileList: FileItemType[]
	setFileList: React.Dispatch<React.SetStateAction<FileItemType[]>>
	isLoading: boolean
	fileUploadType: Object
	noticeType: CmmoCodeShortType[]
}

const BoardWrite: React.FC<BoardWriteProps> = ({
	dialogOpen,
	dialogClose,
	dialogSave,
	data,
	setData,
	bbsType,
	title,
	errors,
	fileList,
	setFileList,
	isLoading,
	fileUploadType,
	noticeType,
}) => {
	const themeMode = useSelector(selectDisplayMode)

	// 모든 필드를 로컬 상태로 관리 (저장 시에만 data에 동기화)
	const currentContentRef = useRef<string>(data.content || '')
	const [localTitle, setLocalTitle] = useState<string>(data.title || '')
	const [localCode, setLocalCode] = useState<string>(data.code || '0')
	const [localStartDate, setLocalStartDate] = useState<string>(
		data.startDate || ''
	)
	const [localEndDate, setLocalEndDate] = useState<string>(data.endDate || '')
	const [localRegId, setLocalRegId] = useState<string>(data.regId || '')
	const [dateError, setDateError] = useState<string>('')

	const [localType, setLocalType] = useState<string>(data.type || '')

	// 로컬 에러 상태 관리 (props로 받은 errors를 복사하여 로컬에서 관리)
	const [localErrors, setLocalErrors] = useState<FieldErrors<any>>({})

	// 제목 변경 핸들러
	const handleTitleChange = useCallback((value: string) => {
		setLocalTitle(value)
		// 제목 변경 시 title 에러 클리어
		setLocalErrors((prev) => {
			const { title, ...rest } = prev
			return rest
		})
	}, [])

	// 다이얼로그가 열릴 때만 초기값 동기화
	useEffect(() => {
		console.log(data)
		if (dialogOpen) {
			setLocalTitle(data.title || '')
			setLocalCode(data.code || '0')
			setLocalStartDate(data.startDate || '')
			setLocalEndDate(data.endDate || '')
			setLocalRegId(data.regId || '')
			currentContentRef.current = data.content || ''
			setLocalType(data.type || '')
			setDateError('') // 다이얼로그 열 때 에러 초기화
			setLocalErrors({}) // 다이얼로그 열 때 로컬 에러 초기화
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dialogOpen])

	// errors props가 변경되면 localErrors에 동기화
	useEffect(() => {
		if (errors) {
			setLocalErrors(errors)
		}
	}, [errors])

	// 저장 전에 모든 로컬 상태를 data에 동기화
	const handleSave = useCallback(() => {
		// 날짜 검증: 시작일이 종료일보다 큰지 확인
		if (localStartDate && localEndDate) {
			const startDate = new Date(localStartDate)
			const endDate = new Date(localEndDate)

			if (startDate > endDate) {
				setDateError('시작일은 종료일보다 이전이어야 합니다.')
				return // 저장 중단
			}
		}

		// 날짜 에러 초기화
		setDateError('')

		// 모든 필드를 한 번에 업데이트
		setData({
			...data,
			title: localTitle,
			code: localCode,
			startDate: localStartDate,
			endDate: localEndDate,
			content: currentContentRef.current,
			regId: localRegId,
			type: localType,
		})

		// 약간의 지연 후 실제 저장 함수 호출
		setTimeout(() => {
			dialogSave()
		}, 100)
	}, [
		data,
		localTitle,
		localCode,
		localStartDate,
		localEndDate,
		localRegId,
		dialogSave,
		setData,
		localType,
	])

	useEffect(() => {}, [data])
	return (
		<>
			<Dialog
				open={dialogOpen}
				onClose={dialogClose}
				slots={{
					transition: Transition,
				}}
				fullScreen
			>
				<AppBar sx={{ position: 'sticky' }}>
					<Toolbar>
						<Typography
							sx={{ flex: 1 }}
							variant="h6"
							component="div"
						>
							{title}
						</Typography>
						<IconButton
							edge="end"
							color="inherit"
							onClick={dialogClose}
							aria-label="close"
						>
							<CloseIcon />
						</IconButton>
					</Toolbar>
				</AppBar>
				<DialogContent>
					<Grid
						container
						spacing={1}
					>
						{/* <Grid size={{ xs: 12, md: 3 }}>
							<TextField
								label="게시물 구분"
								select
								fullWidth
								value={localCode}
								onChange={(e) => {
									setLocalCode(e.target.value)
								}}
								sx={{ mb: 1 }}
								error={!!errors?.code}
								helperText={errors?.code?.message as string}
							>
								<MenuItem value="0">선택하세요.</MenuItem>
								{bbsType.map((row: any, index: number) => {
									return (
										<MenuItem
											key={index}
											value={row.cmmnCd}
										>
											{row.cmmnNm}
										</MenuItem>
									)
								})}
							</TextField>
						</Grid> */}

						<Grid size={{ xs: 12, md: 3 }}>
							<TextField
								label="분류"
								select
								fullWidth
								value={localType}
								onChange={(e) => {
									setLocalType(e.target.value)
									// 분류 변경 시 type 에러 클리어
									setLocalErrors((prev) => {
										const { type, ...rest } = prev
										return rest
									})
								}}
								sx={{ mb: 1 }}
								error={!!localErrors?.type}
								helperText={localErrors?.type?.message as string}
							>
								<MenuItem value="0">선택하세요.</MenuItem>
								{noticeType.map((row: any, index: number) => {
									return (
										<MenuItem
											key={index}
											value={row.cmmnNm}
										>
											{row.cmmnNm}
										</MenuItem>
									)
								})}
							</TextField>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<TextField
								fullWidth
								label="제목"
								value={localTitle}
								sx={{ mb: 1 }}
								onChange={(e) => {
									handleTitleChange(e.target.value)
								}}
								error={!!localErrors?.title}
								helperText={localErrors?.title?.message as string}
							/>
						</Grid>

						<Grid
							size={6}
							sx={{ display: 'none' }}
						>
							<DateTimePicker
								label="시작일"
								value={localStartDate ? dayjs(localStartDate) : null}
								onChange={(e: any) => {
									if (e) {
										setLocalStartDate(e.format('YYYY-MM-DD HH:mm:ss'))
										setDateError('') // 날짜 변경 시 에러 초기화
									}
								}}
								sx={{ width: '100%' }}
								slotProps={{
									textField: {
										error: !!localErrors?.startDate,
										helperText: localErrors?.startDate?.message as string,
									},
								}}
							/>
						</Grid>
						<Grid
							size={6}
							sx={{ display: 'none' }}
						>
							<DateTimePicker
								label="종료일"
								value={localEndDate ? dayjs(localEndDate) : null}
								onChange={(e: any) => {
									if (e) {
										setLocalEndDate(e.format('YYYY-MM-DD HH:mm:ss'))
										setDateError('') // 날짜 변경 시 에러 초기화
									}
								}}
								sx={{ width: '100%' }}
								slotProps={{
									textField: {
										error: !!localErrors?.endDate,
										helperText: localErrors?.endDate?.message as string,
									},
								}}
							/>
						</Grid>
						{dateError && (
							<Grid size={12}>
								<Alert
									severity="error"
									sx={{ mt: 1 }}
								>
									{dateError}
								</Alert>
							</Grid>
						)}
						<Grid
							size={12}
							sx={{
								mt: 1,
								'&>*': {
									color: themeMode.isDarkMode ? '#000' : '#000',
								},
							}}
						>
							<div
								style={{
									backgroundColor: '#fff',
									border: localErrors?.content
										? '2px solid #d32f2f'
										: '1px solid #d0d7de',
									borderRadius: '6px',
									position: 'relative',
									overflow: 'hidden',
								}}
								className="lexical-editor-container"
							>
								{dialogOpen && (
									<LexicalEditorComponent
										initialContent={data.content || ''}
										onContentChange={(content: string) => {
											// 에디터 콘텐츠만 별도로 관리하고, 저장 시에만 data와 동기화
											currentContentRef.current = content
											// 콘텐츠 변경 시 content 에러 클리어
											setLocalErrors((prev) => {
												const { content, ...rest } = prev
												return rest
											})
										}}
									/>
								)}
							</div>
							{localErrors?.content && (
								<div
									style={{
										color: '#d32f2f',
										fontSize: '0.75rem',
										marginTop: '4px',
										marginLeft: '14px',
										lineHeight: '1.66',
									}}
								>
									{localErrors.content.message as string}
								</div>
							)}
						</Grid>
						<Grid
							size={12}
							sx={{ mt: 1 }}
						>
							<Alert
								icon={<CheckIcon fontSize="inherit" />}
								severity="success"
								sx={{ mb: 2 }}
							>
								첨부파일
							</Alert>
							<FileUploader
								filePosition={''}
								orderNo={1}
								fileType={fileUploadType}
								isMultiple={false}
								fileList={fileList}
								setFileList={setFileList}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<Divider />
				<DialogActions>
					<Button
						variant="contained"
						onClick={handleSave}
						color="primary"
						size="small"
						startIcon={<SaveAsTwoToneIcon />}
						disabled={Object.keys(localErrors || {}).length > 0 || isLoading}
					>
						저장
					</Button>
					<Button
						variant="contained"
						onClick={dialogClose}
						color="secondary"
						size="small"
						startIcon={<PhonelinkEraseIcon />}
					>
						취소
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default BoardWrite
